import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import { articleThumbImages } from '@/mocks/articleImages';
import { siteConfig } from '@/mocks/navigation';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import JsonLd from '@/components/feature/JsonLd';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === slug);

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://haolianxi.com/' },
      { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://haolianxi.com/category/${slug}` },
    ],
  };

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteConfig.name}`,
    description: currentCategory.description,
    url: `https://haolianxi.com/category/${slug}`,
    breadcrumb: breadcrumbJsonLd,
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - ${siteConfig.name}`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},人脉管理,${siteConfig.name},职业发展,商务关系`}
        canonicalUrl={`https://haolianxi.com/category/${slug}`}
        ogTitle={`${currentCategory.name} - ${siteConfig.name}`}
        ogDescription={currentCategory.description}
        ogUrl={`https://haolianxi.com/category/${slug}`}
      />
      <JsonLd data={pageJsonLd} />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800">{currentCategory.name}</span>
              </nav>

              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                {currentCategory.description}
              </p>
            </div>
          </div>

          <div className="w-full bg-background-50 border-b border-background-200 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 py-3 overflow-x-auto flex-nowrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 cursor-pointer ${
                      slug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-200">
                      <img
                        src={articleThumbImages[article.slug]}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
                        <span>{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-4 flex-wrap">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2.5 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                  <i className="ri-inbox-line text-4xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
                <Link
                  to="/category/knowledge"
                  className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600 transition-colors duration-200"
                >
                  查看人脉百科
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}